import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import Navbar from "../components/Navbar";
import { useLang } from "../contexts/LanguageContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { ArrowRight, ArrowLeft, Flag, Clock, AlertTriangle, ShieldAlert, Eye, Footprints } from "lucide-react";
import ReferenceLink from "@/components/ReferenceLink";
import { toast } from "sonner";

const PRACTICE_DURATION = null; // untimed
// Official Norwegian Class B theory test: 90 minutes for 45 questions.
const MOCK_DURATION_SECONDS = 90 * 60;

export default function Quiz() {
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [params] = useSearchParams();
  const mode = params.get("mode") || "practice";
  const topic = params.get("topic");
  const subtopic = params.get("subtopic");
  const count = parseInt(params.get("count") || "10", 10);

  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [startedAt] = useState(Date.now());
  const [remaining, setRemaining] = useState(mode === "mock" ? MOCK_DURATION_SECONDS : null);
  const [loading, setLoading] = useState(true);
  const submittedRef = useRef(false);

  useEffect(() => {
    let url;
    if (mode === "mock") {
      url = `/questions/mock?lang=${lang}`;
    } else if (mode === "nvdb") {
      url = `/questions/nvdb-quiz?lang=${lang}&count=${count}`;
    } else {
      const qp = new URLSearchParams({ lang, count: String(count) });
      if (topic) qp.set("topic", topic);
      if (subtopic) qp.set("subtopic", subtopic);
      url = `/questions/practice?${qp.toString()}`;
    }
    api.get(url).then(r => {
      setQuestions(r.data);
      setAnswers(new Array(r.data.length).fill(null));
      if (!r.data?.length) {
        toast.error(t("noQuestionsForTopic") || "No questions available for this selection yet.");
      }
    }).catch((e) => {
      const msg = e?.response?.status === 503
        ? (t("nvdbError") || "NVDB temporarily unavailable. Try again in a moment.")
        : (t("loadFailed") || "Couldn't load the quiz. Please try again.");
      toast.error(msg);
    }).finally(()=>setLoading(false));
  }, [mode, lang, topic, subtopic, count]);

  // Mock timer
  useEffect(() => {
    if (mode !== "mock" || remaining === null) return;
    if (remaining <= 0) { submit(); return; }
    const id = setInterval(() => setRemaining(r => (r ?? 0) - 1), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, remaining]);

  const q = questions[idx];
  const progress = useMemo(() => questions.length ? ((idx) / questions.length) * 100 : 0, [idx, questions.length]);

  const choose = (i) => {
    if (mode === "practice" && answers[idx] !== null) return; // lock after first answer for instant feedback
    const next = [...answers]; next[idx] = i; setAnswers(next);
  };

  const goNext = () => {
    if (idx < questions.length - 1) setIdx(i => i + 1);
    else submit();
  };

  const submit = async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    const duration_seconds = Math.round((Date.now() - startedAt) / 1000);

    // NVDB mode is graded client-side because the question pool is dynamic
    // and not persisted in MongoDB. We stash the attempt in sessionStorage
    // so Results.jsx can render the same UI as a normal attempt.
    if (mode === "nvdb") {
      const results = questions.map((q, i) => ({
        question_id: q.question_id, topic: q.topic,
        user_answer: answers[i], correct_index: q.correct_index,
        is_correct: answers[i] === q.correct_index,
        question: q.question, options: q.options, explanation: q.explanation,
        image: q.image, visual_type: q.visual_type, reference: q.reference,
        scenario_text: q.scenario_text, question_type: q.question_type,
      }));
      const correct = results.filter(r => r.is_correct).length;
      const total = results.length;
      const accuracy = total ? Math.round((correct / total) * 100) : 0;
      const attempt = {
        attempt_id: `nvdb_${Date.now()}`,
        mode: "nvdb", topic: "speed_positioning", language: lang,
        total, correct, accuracy, passed: accuracy >= 70,
        pass_threshold: "70%", duration_seconds,
        created_at: new Date().toISOString(), results,
      };
      sessionStorage.setItem(attempt.attempt_id, JSON.stringify(attempt));
      navigate(`/results/${attempt.attempt_id}`, { replace: true });
      return;
    }

    try {
      const res = await api.post("/quiz/submit", {
        mode, topic: topic || null, language: lang,
        question_ids: questions.map(x => x.question_id),
        answers, duration_seconds,
      });
      navigate(`/results/${res.data.attempt_id}`, { replace: true });
    } catch {
      submittedRef.current = false;
      toast.error(t("submitFailed") || "Submission failed. Please try again.");
    }
  };

  if (loading || !q) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar/>
        <div className="max-w-3xl mx-auto p-10 text-center text-gray-500">{t("loading")}</div>
      </div>
    );
  }

  const answered = answers[idx];
  const showFeedback = mode === "practice" && answered !== null;

  const mmss = (s) => {
    const m = Math.floor(s/60), ss = s%60;
    return `${String(m).padStart(2,"0")}:${String(ss).padStart(2,"0")}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar/>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between mb-4 text-sm">
          <div className="text-gray-600">
            {t("question")} <span className="font-semibold text-gray-900" data-testid="question-counter">{idx+1}</span> {t("of")} {questions.length}
          </div>
          {mode === "mock" && (
            <div data-testid="mock-timer" className={`flex items-center gap-1.5 font-mono font-semibold ${remaining < 60 ? "text-red-600" : "text-gray-900"}`}>
              <Clock className="w-4 h-4"/> {mmss(remaining ?? 0)}
            </div>
          )}
        </div>
        <Progress value={progress} className="h-2 mb-8"/>

        <Card className="p-6 sm:p-8 bg-white border border-gray-200 mb-6">
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <Badge variant="outline" className="text-xs">{q.topic}</Badge>
            {q.question_type && (
              <Badge
                data-testid="question-type-badge"
                className="text-[10px] uppercase tracking-wide bg-fjord/10 text-fjord border-fjord/20 hover:bg-fjord/10"
                variant="outline"
              >
                {q.question_type}
              </Badge>
            )}
          </div>
          {q.scenario_text && (
            <div
              data-testid="question-scenario"
              className="mb-5 flex items-start gap-3 border-s-4 border-fjord/40 bg-fjord/5 rounded-r-md p-3"
            >
              <Eye className="w-4 h-4 text-fjord mt-0.5 shrink-0" />
              <p className="text-sm italic text-gray-700 leading-relaxed">
                {q.scenario_text}
              </p>
            </div>
          )}
          {q.image && (
            <div data-testid="question-image" className="mb-5 flex justify-center">
              <div
                className={`bg-gray-50 rounded-lg p-4 grid place-items-center border border-gray-200 ${
                  q.visual_type === "road_marking" || q.visual_type === "action_scene"
                    ? "w-64 h-48 sm:w-80 sm:h-56"
                    : q.visual_type === "traffic_light"
                    ? "w-24 h-56"
                    : "w-40 h-40 sm:w-48 sm:h-48"
                }`}
              >
                <img src={q.image} alt="Visual" className="max-w-full max-h-full object-contain"/>
              </div>
            </div>
          )}
          <h2 data-testid="question-text" className="font-heading text-xl sm:text-2xl font-semibold leading-relaxed mb-6">
            {q.question}
          </h2>
          <div className="space-y-3">
            {q.options.map((opt, i) => {
              const isSelected = answered === i;
              const isCorrect = mode === "practice" && answered !== null && i === q.correct_index;
              const isWrong = mode === "practice" && answered === i && i !== q.correct_index;
              let cls = "border-gray-200 hover:border-fjord/40 hover:bg-fjord/5";
              if (isCorrect) cls = "border-emerald-500 bg-emerald-50";
              else if (isWrong) cls = "border-red-500 bg-red-50";
              else if (isSelected) cls = "border-fjord bg-fjord/5";
              return (
                <button
                  key={i}
                  data-testid={`option-${i}`}
                  disabled={showFeedback}
                  onClick={() => choose(i)}
                  className={`w-full text-start px-5 py-4 rounded-lg border-2 transition-colors ${cls}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-7 h-7 rounded-md grid place-items-center text-sm font-semibold ${isSelected||isCorrect?"bg-fjord text-white":"bg-gray-100 text-gray-700"}`}>
                      {String.fromCharCode(65+i)}
                    </div>
                    <span className="flex-1">{opt}</span>
                  </div>
                </button>
              );
            })}
          </div>

          {showFeedback && (
            <div className="mt-5 space-y-3">
              <div className={`p-4 rounded-lg ${answered === q.correct_index ? "bg-emerald-50 border border-emerald-200" : "bg-red-50 border border-red-200"}`}>
                <div className={`font-semibold mb-1 ${answered === q.correct_index ? "text-emerald-700" : "text-red-700"}`}>
                  {answered === q.correct_index ? t("correct") : t("incorrect")}
                </div>
                {q.explanation && <p className="text-sm text-gray-700">{q.explanation}</p>}
                {q.reference && <ReferenceLink reference={q.reference} />}
              </div>
              {q.driver_action && (
                <div
                  data-testid="question-driver-action"
                  className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 flex items-start gap-2"
                >
                  <Footprints className="w-4 h-4 text-emerald-700 shrink-0 mt-0.5" />
                  <div className="text-sm text-emerald-900">
                    <div className="font-semibold text-xs uppercase tracking-wide mb-0.5">
                      {t("driverActionLabel")}
                    </div>
                    {q.driver_action}
                  </div>
                </div>
              )}
              {q.common_mistake && (
                <div
                  data-testid="question-common-mistake"
                  className="p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-2"
                >
                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-900">
                    <div className="font-semibold text-xs uppercase tracking-wide mb-0.5">
                      {t("commonMistakeLabel")}
                    </div>
                    {q.common_mistake}
                  </div>
                </div>
              )}
              {q.risk_explanation && (
                <div
                  data-testid="question-risk-explanation"
                  className="p-3 rounded-lg bg-rose-50 border border-rose-200 flex items-start gap-2"
                >
                  <ShieldAlert className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                  <div className="text-sm text-rose-900">
                    <div className="font-semibold text-xs uppercase tracking-wide mb-0.5">
                      {t("riskWhyMattersLabel")}
                    </div>
                    {q.risk_explanation}
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>

        <div className="flex items-center justify-between gap-3">
          <Button variant="outline" data-testid="prev-button" disabled={idx===0} onClick={()=>setIdx(i=>Math.max(0,i-1))}>
            <ArrowLeft className="w-4 h-4 me-1.5 rtl:rotate-180"/> {t("previous")}
          </Button>
          <div className="flex items-center gap-2">
            {mode === "mock" && (
              <Button variant="ghost" data-testid="submit-mock" onClick={submit}>
                <Flag className="w-4 h-4 me-1.5"/> {t("finish")}
              </Button>
            )}
            <Button data-testid="next-button" className="bg-fjord hover:bg-fjord-700" onClick={goNext}>
              {idx === questions.length - 1 ? t("submit") : t("next")}
              <ArrowRight className="w-4 h-4 ms-1.5 rtl:rotate-180"/>
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
